// src/pages/trading-portal/TradeExecution/TradeExecutionDashboard.js
// Trade Execution Engine dashboard overview

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Alert,
    Button,
    Chip,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    Receipt as ReceiptIcon,
    Memory as MemoryIcon,
    Shield as ShieldIcon,
    FactCheck as FactCheckIcon,
    NavigateNext as NavigateNextIcon,
    Bolt as BoltIcon,
    TrendingUp as TrendingUpIcon,
    AttachMoney as AttachMoneyIcon,
    PowerSettingsNew as PowerSettingsNewIcon,
    BrokenImage as BrokenImageIcon,
    GppBad as GppBadIcon,
    Paid as PaidIcon,
    AccountBalance as AccountBalanceIcon,
    ShoppingCart as ShoppingCartIcon,
    CheckCircle as CheckCircleIcon,
    ThumbUp as ThumbUpIcon,
    ThumbDown as ThumbDownIcon,
    Percent as PercentIcon,
    SmartToy as SmartToyIcon,
    Psychology as PsychologyIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    TEEOrderStatusLabels,
    TEEOrderStatusColors,
    TEEOrderTypeLabels,
    OrderSideLabels,
    OrderSideColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

// ─── Navigation items ────────────────────────────────────────────────

const NAV_ITEMS = [
    {
        label: 'Order Management',
        icon: ReceiptIcon,
        path: '/trading-portal/trade-execution/orders',
        color: '#3b82f6',
        description: 'Create, track, and manage orders with multi-leg support',
    },
    {
        label: 'Execution Algorithms',
        icon: MemoryIcon,
        path: '/trading-portal/trade-execution/algorithms',
        color: '#8b5cf6',
        description: 'TWAP, VWAP, and adaptive execution strategies',
    },
    {
        label: 'Risk Controls',
        icon: ShieldIcon,
        path: '/trading-portal/trade-execution/risk-controls',
        color: '#ef4444',
        description: 'Pre-trade risk profiles, limits, and kill switch',
    },
    {
        label: 'Post-Trade Processing',
        icon: FactCheckIcon,
        path: '/trading-portal/trade-execution/post-trade',
        color: '#10b981',
        description: 'Confirmations, reconciliation, TCA, and tax lots',
    },
    {
        label: 'Trading Bots',
        icon: SmartToyIcon,
        path: '/trading-portal/trade-execution/trading-bots',
        color: '#8b5cf6',
        description: 'Automated trading bot configuration and monitoring',
    },
    {
        label: 'AI Trading Agents',
        icon: PsychologyIcon,
        path: '/trading-portal/trade-execution/ai-agents',
        color: '#06b6d4',
        description: 'AI-powered trading agents using Gemini LLM for quantitative analysis',
    },
];

// ─── Sub-components ──────────────────────────────────────────────────

const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
        }}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '14px',
                        bgcolor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon sx={{ color, fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{ fontFamily, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}
                    >
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="caption"
                            sx={{ fontFamily, color: '#94a3b8', fontWeight: 500 }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const StatCardSkeleton = () => (
    <Card sx={{ height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: '14px' }} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" sx={{ fontSize: '0.8rem' }} />
                    <Skeleton variant="text" width="40%" sx={{ fontSize: '1.5rem' }} />
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Main component ──────────────────────────────────────────────────

const TradeExecutionDashboard = () => {
    const navigate = useNavigate();

    const [canView, setCanView] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dashboardData, setDashboardData] = useState(null);

    // Permission check
    useEffect(() => {
        const checkPerms = async () => {
            const view = await hasPermission('tr-tee-dashboard', 'view');
            setCanView(view);
        };
        checkPerms();
    }, []);

    // Fetch dashboard data
    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axiosInstance.get('/api/trade-execution/dashboard');
            const data = response.data?.data || response.data || {};
            setDashboardData({
                open_orders: data.open_orders || 0,
                todays_fills: data.todays_fills || 0,
                daily_pnl: data.daily_pnl || {},
                kill_switch_active: data.kill_switch_active || false,
                pending_recon_breaks: data.pending_recon_breaks || 0,
                recent_risk_failures: data.recent_risk_failures || 0,
                recent_orders: data.recent_orders || [],
                total_commission_today: data.total_commission_today || 0,
                total_fees_today: data.total_fees_today || 0,
                orders_placed_today: data.orders_placed_today || 0,
                orders_filled_today: data.orders_filled_today || 0,
                winning_trades: data.winning_trades || 0,
                losing_trades: data.losing_trades || 0,
                win_rate: data.win_rate || 0,
                active_algo_executions: data.active_algo_executions || 0,
                active_bots: data.active_bots || 0,
                total_bots: data.total_bots || 0,
            });
        } catch (err) {
            console.error('Failed to fetch trade execution dashboard:', err);
            setError(err.response?.data?.message || 'Failed to load dashboard data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canView) {
            fetchDashboard();
        }
    }, [canView, fetchDashboard]);

    // ── Helpers ──────────────────────────────────────────────────────

    const formatCurrency = (value) => {
        if (value == null) return '--';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '--';
        return new Date(dateStr).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // ── Permission gate ──────────────────────────────────────────────

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ fontFamily }}>
                    You do not have permission to view the Trade Execution Engine Dashboard.
                </Alert>
            </Box>
        );
    }

    // ── Render ───────────────────────────────────────────────────────

    const netPnl = dashboardData?.daily_pnl?.net_pnl ?? 0;
    const pnlColor = netPnl >= 0 ? '#22c55e' : '#ef4444';

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* ── Gradient Header ───────────────────────────────────── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e1e2e 0%, #2d1b4e 100%)',
                    borderRadius: 3,
                    p: 4,
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -40,
                        right: -40,
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        background: `${ACCENT}15`,
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -20,
                        left: '30%',
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: `${ACCENT}10`,
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <BoltIcon sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                    Trade Execution Engine
                                </Typography>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', mt: 0.25 }}>
                                    Order management, execution algorithms, risk controls, and post-trade processing
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    {/* Kill Switch Badge */}
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '14px', px: 2.5, py: 1.5, textAlign: 'center', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.15)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
                            <PowerSettingsNewIcon sx={{ fontSize: 18, color: loading ? '#fff' : dashboardData?.kill_switch_active ? '#ef4444' : '#22c55e' }} />
                            <Typography sx={{ fontFamily, fontWeight: 800, color: '#fff', fontSize: '1.1rem', lineHeight: 1 }}>
                                {loading ? '--' : dashboardData?.kill_switch_active ? 'Active' : 'Inactive'}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 500, mt: 0.25 }}>
                            Kill Switch
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* ── Error Alert ──────────────────────────────────── */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={fetchDashboard}
                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* ── Stat Cards ──────────────────────────────────── */}
                <Grid container spacing={2.5} sx={{ mb: 4 }}>
                    {/* Open Orders */}
                    <Grid item xs={12} sm={6} md={2}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={ReceiptIcon}
                                title="Open Orders"
                                value={dashboardData?.open_orders ?? 0}
                                color="#3b82f6"
                                subtitle="Currently active"
                            />
                        )}
                    </Grid>

                    {/* Today's Fills */}
                    <Grid item xs={12} sm={6} md={2}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={TrendingUpIcon}
                                title="Today's Fills"
                                value={dashboardData?.todays_fills ?? 0}
                                color="#8b5cf6"
                                subtitle="Filled today"
                            />
                        )}
                    </Grid>

                    {/* Daily P&L */}
                    <Grid item xs={12} sm={6} md={2}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={AttachMoneyIcon}
                                title="Daily P&L"
                                value={
                                    <Typography
                                        component="span"
                                        variant="h5"
                                        sx={{
                                            fontFamily,
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                            color: pnlColor,
                                        }}
                                    >
                                        {formatCurrency(netPnl)}
                                    </Typography>
                                }
                                color={pnlColor}
                                subtitle="Net P&L today"
                            />
                        )}
                    </Grid>

                    {/* Kill Switch */}
                    <Grid item xs={12} sm={6} md={2}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={PowerSettingsNewIcon}
                                title="Kill Switch"
                                value={
                                    <Chip
                                        label={dashboardData?.kill_switch_active ? 'Active' : 'Inactive'}
                                        size="small"
                                        sx={{
                                            fontFamily,
                                            fontWeight: 700,
                                            fontSize: '0.8rem',
                                            height: 26,
                                            bgcolor: dashboardData?.kill_switch_active ? '#fee2e2' : '#dcfce7',
                                            color: dashboardData?.kill_switch_active ? '#ef4444' : '#22c55e',
                                        }}
                                    />
                                }
                                color={dashboardData?.kill_switch_active ? '#ef4444' : '#22c55e'}
                                subtitle="Trading halt status"
                            />
                        )}
                    </Grid>

                    {/* Pending Recon Breaks */}
                    <Grid item xs={12} sm={6} md={2}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={BrokenImageIcon}
                                title="Recon Breaks"
                                value={dashboardData?.pending_recon_breaks ?? 0}
                                color="#f97316"
                                subtitle="Pending resolution"
                            />
                        )}
                    </Grid>

                    {/* Risk Failures (24h) */}
                    <Grid item xs={12} sm={6} md={2}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={GppBadIcon}
                                title="Risk Failures"
                                value={dashboardData?.recent_risk_failures ?? 0}
                                color="#ef4444"
                                subtitle="Last 24 hours"
                            />
                        )}
                    </Grid>
                </Grid>

                {/* ── Additional Metrics ─────────────────────────── */}
                <Grid container spacing={2.5} sx={{ mb: 4 }}>
                    {/* Commission Today */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={PaidIcon}
                                title="Commission Today"
                                value={formatCurrency(dashboardData?.total_commission_today)}
                                color="#6366f1"
                                subtitle="Total commissions"
                            />
                        )}
                    </Grid>

                    {/* Fees Today */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={AccountBalanceIcon}
                                title="Fees Today"
                                value={formatCurrency(dashboardData?.total_fees_today)}
                                color="#0891b2"
                                subtitle="Total fees"
                            />
                        )}
                    </Grid>

                    {/* Orders Placed Today */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={ShoppingCartIcon}
                                title="Orders Placed"
                                value={dashboardData?.orders_placed_today ?? 0}
                                color="#8b5cf6"
                                subtitle="Placed today"
                            />
                        )}
                    </Grid>

                    {/* Orders Filled Today */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={CheckCircleIcon}
                                title="Orders Filled"
                                value={dashboardData?.orders_filled_today ?? 0}
                                color="#10b981"
                                subtitle="Filled today"
                            />
                        )}
                    </Grid>

                    {/* Winning Trades */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={ThumbUpIcon}
                                title="Winning Trades"
                                value={dashboardData?.winning_trades ?? 0}
                                color="#22c55e"
                                subtitle="Profitable trades"
                            />
                        )}
                    </Grid>

                    {/* Losing Trades */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={ThumbDownIcon}
                                title="Losing Trades"
                                value={dashboardData?.losing_trades ?? 0}
                                color="#ef4444"
                                subtitle="Unprofitable trades"
                            />
                        )}
                    </Grid>

                    {/* Win Rate */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={PercentIcon}
                                title="Win Rate"
                                value={`${parseFloat(dashboardData?.win_rate ?? 0).toFixed(1)}%`}
                                color={parseFloat(dashboardData?.win_rate ?? 0) >= 50 ? '#22c55e' : '#ef4444'}
                                subtitle="Trade success rate"
                            />
                        )}
                    </Grid>

                    {/* Active Algo Executions */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={SmartToyIcon}
                                title="Active Algos"
                                value={dashboardData?.active_algo_executions ?? 0}
                                color="#f59e0b"
                                subtitle="Running algorithms"
                            />
                        )}
                    </Grid>

                    {/* Active Bots */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={SmartToyIcon}
                                title="Active Bots"
                                value={dashboardData?.active_bots ?? 0}
                                color="#8b5cf6"
                                subtitle="Currently running"
                            />
                        )}
                    </Grid>

                    {/* Total Bots */}
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={SmartToyIcon}
                                title="Total Bots"
                                value={dashboardData?.total_bots ?? 0}
                                color="#6366f1"
                                subtitle="All configured bots"
                            />
                        )}
                    </Grid>
                </Grid>

                {/* ── Navigation Cards ────────────────────────────────── */}
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', mb: 2 }}>
                        Sub-Modules
                    </Typography>
                    <Grid container spacing={2}>
                        {NAV_ITEMS.map((item) => (
                            <Grid item xs={12} sm={6} md={3} key={item.path}>
                                <Card
                                    sx={{
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: 'none',
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: item.color,
                                            boxShadow: `0 4px 20px ${item.color}20`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                    onClick={() => navigate(item.path)}
                                >
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: '14px',
                                                        bgcolor: `${item.color}15`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <item.icon sx={{ color: item.color, fontSize: 24 }} />
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.78rem', fontWeight: 500 }}>
                                                        {item.description}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <NavigateNextIcon sx={{ color: '#cbd5e1', fontSize: 22 }} />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* ── Recent Orders Table ──────────────────────────── */}
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ReceiptIcon sx={{ color: '#3b82f6', fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Recent Orders
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/trading-portal/trade-execution/orders')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: ACCENT, fontSize: '0.78rem', '&:hover': { bgcolor: `${ACCENT}08` } }}
                                    >
                                        View All
                                    </Button>
                                </Box>

                                <Box sx={{ px: 1, pb: 2 }}>
                                    {loading ? (
                                        Array.from({ length: 5 }).map((_, i) => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 1.5 }}>
                                                <Skeleton variant="circular" width={42} height={42} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="70%" sx={{ fontSize: '0.85rem' }} />
                                                    <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
                                                </Box>
                                            </Box>
                                        ))
                                    ) : !dashboardData?.recent_orders?.length ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <ReceiptIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>
                                                No recent orders
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Orders will appear here once submitted
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>Symbol</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>Side</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>Type</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }} align="right">Qty</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>Status</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 700, color: '#64748b', fontSize: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>Submitted At</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {dashboardData.recent_orders.slice(0, 10).map((order, index) => (
                                                        <TableRow key={order.id || index} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem', borderBottom: '1px solid #f1f5f9' }}>
                                                                {order.symbol || '--'}
                                                            </TableCell>
                                                            <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                <Chip
                                                                    label={OrderSideLabels[order.side] || order.side || '--'}
                                                                    size="small"
                                                                    color={OrderSideColors[order.side] || 'default'}
                                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' }}>
                                                                {TEEOrderTypeLabels[order.order_type] || order.order_type || '--'}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem', borderBottom: '1px solid #f1f5f9' }}>
                                                                {order.quantity != null ? order.quantity.toLocaleString() : '--'}
                                                            </TableCell>
                                                            <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                <Chip
                                                                    label={TEEOrderStatusLabels[order.status] || order.status || '--'}
                                                                    size="small"
                                                                    color={TEEOrderStatusColors[order.status] || 'default'}
                                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' }}>
                                                                {formatDateTime(order.submitted_at)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default TradeExecutionDashboard;
